import React from "react";
import { useTranslation } from "react-i18next";
import {
  getComponentAmount,
  getOutcomePolicyFormValue,
  getOutcomeRegistryRows,
  OutcomeRegistryRow,
} from "../../../utils/opportunities/outcomeRegistryHelpers";
import {
  EMPTY_VALUE,
  FinancialComponent,
  findComponentByType,
  formatAmount,
  formatChoiceText,
  getPolicyOutcomes,
  OutcomeData,
  getSignedComponentAmount,
  ViewStep,
} from "./opportunityViewHelpers";
import { CARD_SHADOW_CLASS } from "./opportunityViewParts";

const DEFAULT_COMPONENT_FIELD_PATH = "components.componentType";

const COMPONENT_ROW_STYLES: Record<string, string> = {
  bonus: "bg-emerald-50",
  expense: "bg-rose-50",
  penalty: "bg-amber-50",
};

type OptionLabelResolver = (fieldPath: string, value: string) => string;

interface FinancialComponentListProps {
  items: FinancialComponent[];
  baseAmount: number;
  currencySymbol: string;
  getOptionLabel: OptionLabelResolver;
  getFundingStageLabel: (value: string) => string;
  showFundingStage: boolean;
}

export const FinancialComponentList: React.FC<FinancialComponentListProps> = ({
  items,
  baseAmount,
  currencySymbol,
  getOptionLabel,
  getFundingStageLabel,
  showFundingStage,
}) => {
  if (!items.length) {
    return <p className="text-sm text-gray-400">{EMPTY_VALUE}</p>;
  }

  return (
    <div className="flex flex-col gap-3 pt-1">
      {items.map((item, index) => {
        const amount = getSignedComponentAmount(
          item,
          getComponentAmount(item, baseAmount),
        );
        const label = item.componentType
          ? getOptionLabel(DEFAULT_COMPONENT_FIELD_PATH, String(item.componentType))
          : EMPTY_VALUE;
        const details = [
          item.expenseDescription,
          showFundingStage && item.fundingStage
            ? getFundingStageLabel(String(item.fundingStage))
            : "",
        ]
          .filter(Boolean)
          .join(" · ");

        return (
          <div
            key={`${item.componentType}-${index}`}
            className={`grid grid-cols-2 items-center gap-x-4 rounded-3xl px-4 py-3.5 ${
              COMPONENT_ROW_STYLES[String(item.kind).toLowerCase()] || "bg-gray-50"
            }`}
          >
            <div className="flex min-w-0 flex-col">
              <span className="break-words text-base font-medium leading-6 text-gray-900">
                {label}
              </span>
              {details && (
                <span className="break-words text-xs font-normal leading-4 text-gray-500">
                  {details}
                </span>
              )}
            </div>
            <span className="whitespace-nowrap text-base font-semibold leading-6 tabular-nums text-gray-900">
              {formatAmount(amount, currencySymbol)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface PayoutPolicyListProps {
  stepData: unknown;
  step: ViewStep;
  baseAmount: number;
  currencySymbol: string;
  components: FinancialComponent[];
  getOptionLabel: OptionLabelResolver;
}

interface OutcomeEntry {
  key: string;
  row: OutcomeRegistryRow;
  value: OutcomeData;
}

const buildOutcomeEntries = (
  outcomes: Record<string, OutcomeData>,
  step: ViewStep,
): OutcomeEntry[] => {
  const registryRows = step.outcomeRegistry
    ? getOutcomeRegistryRows(step.outcomeRegistry)
    : [];
  const registryKeys = new Set(registryRows.map((row) => row.key));

  const registryEntries = registryRows.map((row) => ({
    key: row.key,
    row,
    value: outcomes[row.key] || {},
  }));

  const extraEntries = Object.entries(outcomes)
    .filter(
      ([key, value]) =>
        !registryKeys.has(key) &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value),
    )
    .map(([key, value]) => ({
      key,
      row: {
        key,
        group: "",
        label: value.label,
        baseLocked: value.baseLocked,
        baseIncluded: value.baseIncluded,
        componentsSelectable: true,
        selectionMode: value.selectionMode,
      } as OutcomeRegistryRow,
      value,
    }));

  return [...registryEntries, ...extraEntries];
};

const toAmount = (value: unknown): number | null => {
  const amount = Number(value);
  return value === null || value === undefined || Number.isNaN(amount)
    ? null
    : amount;
};

const PolicyRow: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div
    className={`grid grid-cols-2 gap-x-4 text-base leading-6 text-gray-900 ${
      strong ? "font-bold" : "font-normal"
    }`}
  >
    <span className="min-w-0 break-words">{label}</span>
    <span className="whitespace-nowrap tabular-nums">{value}</span>
  </div>
);

export const PayoutPolicyList: React.FC<PayoutPolicyListProps> = ({
  stepData,
  step,
  baseAmount,
  currencySymbol,
  components,
  getOptionLabel,
}) => {
  const { t } = useTranslation("opportunities");
  const entries = buildOutcomeEntries(getPolicyOutcomes(stepData), step);

  if (!entries.length) {
    return <p className="text-sm text-gray-400">{EMPTY_VALUE}</p>;
  }

  return (
    <div className="flex flex-col gap-3 pt-1">
      {entries.map(({ key, row, value }) => {
        const policyValue = getOutcomePolicyFormValue(row, value);
        const modeValue = value.selectionMode || row.selectionMode;
        const componentFieldPath = row.optionsFrom || DEFAULT_COMPONENT_FIELD_PATH;

        const lines = policyValue.selectedComponents.map((componentType) => {
          const component = findComponentByType(components, componentType);
          return {
            componentType,
            amount: getSignedComponentAmount(
              component,
              getComponentAmount(component, baseAmount),
            ),
          };
        });

        const bruto = toAmount(value.bruto);
        const total = toAmount(value.total);

        if (!policyValue.baseIncluded && !lines.length && bruto === null && total === null) {
          return null;
        }

        return (
          <div
            key={key}
            className={`flex flex-col gap-3 rounded-3xl border border-gray-100 bg-white p-4 ${CARD_SHADOW_CLASS}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col">
                <span className="text-sm font-bold leading-5 text-gray-900">
                  {t(`payout_outcome.${key}`, {
                    defaultValue: row.label || formatChoiceText(key, t),
                  })}
                </span>
                {modeValue && (
                  <span className="text-xs font-normal leading-4 text-gray-500">
                    {t(`payout_selection_mode.${modeValue}`, {
                      defaultValue: formatChoiceText(String(modeValue), t),
                    })}
                  </span>
                )}
              </div>
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium leading-4 ${
                  policyValue.baseLocked
                    ? "bg-blue-200 text-gray-900"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {policyValue.baseLocked
                  ? t("payout_policy.baseLocked")
                  : t("payout_policy.baseEditable")}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {policyValue.baseIncluded && (
                <PolicyRow
                  label={t("payout_policy.baseAmount")}
                  value={formatAmount(baseAmount, currencySymbol)}
                />
              )}
              {lines.map((line) => (
                <PolicyRow
                  key={line.componentType}
                  label={getOptionLabel(componentFieldPath, line.componentType)}
                  value={formatAmount(line.amount, currencySymbol)}
                />
              ))}
            </div>

            {(bruto !== null || total !== null) && (
              <div className="flex flex-col gap-2 border-t border-gray-200 pt-3">
                {bruto !== null && (
                  <PolicyRow
                    strong
                    label={t("payout_policy.bruto")}
                    value={formatAmount(bruto, currencySymbol)}
                  />
                )}
                {total !== null && (
                  <PolicyRow
                    strong
                    label={t("payout_policy.total")}
                    value={formatAmount(total, currencySymbol)}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
